import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { patientAPI } from '../../../utils/api';

const PatientHistory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [chatHistory, setChatHistory] = useState([]);
  const [callHistory, setCallHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('chat');

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchHistory = async () => {
    try {
      const [chatRes, callRes] = await Promise.all([
        patientAPI.getChatHistory(),
        patientAPI.getCallHistory()
      ]);

      if (chatRes.data?.success) {
        setChatHistory(chatRes.data.data || []);
      }

      if (callRes.data?.success) {
        setCallHistory(callRes.data.data || []);
      }
    } catch (error) {
      showError('Failed to fetch history');
    } finally {
      setLoading(false);
    }
  };

  const filteredHistory = selectedTab === 'chat' ? chatHistory : callHistory;

  if (loading) {
    return <div className="text-center py-8">Loading history...</div>;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gold">My History</h1>

      {/* Tab Navigation */}
      <div className="flex space-x-2 bg-gray-800 rounded-xl p-1">
        <button
          onClick={() => setSelectedTab('chat')}
          className={`flex-1 py-3 rounded-lg font-medium ${selectedTab === 'chat'
            ? 'bg-gold text-dark'
            : 'text-gray-400'
            }`}
        >
          Chat History
        </button>
        <button
          onClick={() => setSelectedTab('call')}
          className={`flex-1 py-3 rounded-lg font-medium ${selectedTab === 'call'
            ? 'bg-gold text-dark'
            : 'text-gray-400'
            }`}
        >
          Call History
        </button>
      </div>

      {/* History List */}
      <div className="space-y-4">
        {filteredHistory.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-gray-400">No {selectedTab} history yet</p>
            <p className="text-sm text-gray-500 mt-2">
              {selectedTab === 'chat'
                ? 'Your chat sessions will appear here'
                : 'Your voice/video sessions will appear here'
              }
            </p>
          </div>
        ) : (
          filteredHistory.map((item) => (
            <div key={item._id} className="bg-gray-800 rounded-xl p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold">
                    {selectedTab === 'chat' ? 'Chat Session' : `${item.call_type} Call`}
                  </h3>
                  <p className="text-gray-400">
                    {item.doctor_id?.name || 'Specialist'} • {item.duration || 'N/A'} minutes
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${item.status === 'completed' ? 'bg-green-900 text-green-300' : 'bg-yellow-900 text-yellow-300'
                  }`}>
                  {item.status}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <p>
                  {(() => {
                    const date = new Date(item.createdAt);
                    if (isNaN(date.getTime())) return 'N/A';
                    if (date.getFullYear() < 2000) {
                      const fixed = new Date(Number(item.createdAt) * 1000);
                      return isNaN(fixed.getTime()) ? 'N/A' : fixed.toLocaleString();
                    }
                    return date.toLocaleString();
                  })()}
                </p>
                <button
                  onClick={() => navigate(`/patient/prescription/${item._id}`)}
                  className="bg-gray-700 text-gray-300 px-4 py-2 rounded-lg font-medium hover:bg-gray-600"
                >
                  View Recommendations
                </button>
              </div>

              {selectedTab === 'chat' && item.messages && (
                <div className="mt-3 pt-3 border-t border-gray-700">
                  <p className="text-gray-400 text-sm line-clamp-2">
                    {item.messages.slice(-1)[0]?.content || 'No messages available'}
                  </p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div >
  );
};

export default PatientHistory;
